import { useState, useEffect, useRef, useCallback, memo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import liveStreamService, { LiveStream as LiveStreamType } from '../../services/liveStreamService'
import { Heart, MessageCircle, Share2, Users, ArrowLeft, Send, X, Video, Radio } from 'lucide-react'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'

interface Comment {
  id: string
  userId: number
  username: string
  content: string
  timestamp: number
}

const WatchLiveStream = memo(() => {
  const { streamId } = useParams<{ streamId: string }>()
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuthStore()

  // Stream state
  const [stream, setStream] = useState<LiveStreamType | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isEnded, setIsEnded] = useState(false)

  // Playback state
  const [viewerCount, setViewerCount] = useState(0)
  const [likeCount, setLikeCount] = useState(0)
  const [duration, setDuration] = useState(0)

  // Comments
  const [comments, setComments] = useState<Comment[]>([])
  const [newComment, setNewComment] = useState('')
  const [showComments, setShowComments] = useState(true)

  // Refs
  const videoRef = useRef<HTMLVideoElement>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const pcRef = useRef<RTCPeerConnection | null>(null)
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const commentsEndRef = useRef<HTMLDivElement>(null)

  // Use WebSocket URL - in development, connect directly to backend
  const getWebSocketUrl = useCallback(() => {
    const isDev = import.meta.env.DEV
    if (isDev) {
      return 'ws://localhost:8091/ws/live-stream'
    }
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const host = window.location.host
    return `${protocol}//${host}/ws/live-stream`
  }, [])

  const ICE_SERVERS = [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' }
  ]

  // Fetch stream details
  const fetchStream = useCallback(async () => {
    if (!streamId) return

    try {
      setLoading(true)
      const response = await liveStreamService.getStream(parseInt(streamId))
      if (response.success) {
        const streamData = response.data
        setStream(streamData)
        setViewerCount(streamData.viewerCount || 0)
        setLikeCount(streamData.likeCount || 0)

        if (streamData.status !== 'LIVE') {
          setIsEnded(true)
        }
      } else {
        setError('Stream not found')
      }
    } catch (err) {
      console.error('Error fetching stream:', err)
      setError('Failed to load stream')
    } finally {
      setLoading(false)
    }
  }, [streamId])

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    if (!streamId) return

    const wsUrl = `${getWebSocketUrl()}?streamId=${streamId}&role=viewer&userId=${user?.id || 'anonymous'}`
    wsRef.current = new WebSocket(wsUrl)

    wsRef.current.onopen = () => {
      console.log('WebSocket connected')
      wsRef.current?.send(JSON.stringify({
        type: 'viewer_join',
        streamId: parseInt(streamId!),
        userId: user?.id
      }))

      liveStreamService.joinStream(parseInt(streamId!)).catch(console.error)
    }

    wsRef.current.onmessage = async (event) => {
      try {
        const data = JSON.parse(event.data)
        handleWebSocketMessage(data)
      } catch (error) {
        console.error('Error parsing WebSocket message:', error)
      }
    }

    wsRef.current.onerror = (error) => {
      console.error('WebSocket error:', error)
    }

    wsRef.current.onclose = () => {
      console.log('WebSocket closed')
    }
  }, [streamId, user?.id, getWebSocketUrl])

  // Handle WebSocket messages
  const handleWebSocketMessage = async (data: any) => {
    switch (data.type) {
      case 'viewer_joined':
        console.log('Successfully joined stream')
        break

      case 'offer':
        await handleOffer(data.offer)
        break

      case 'ice_candidate':
        await handleIceCandidate(data.candidate)
        break

      case 'stream_ended':
        setIsEnded(true)
        break

      case 'viewer_count':
        setViewerCount(data.count)
        break

      case 'like':
        setLikeCount(prev => prev + 1)
        showFloatingHeart()
        break

      case 'comment':
        const newComment: Comment = {
          id: Date.now().toString(),
          userId: data.userId,
          username: data.username || 'Anonymous',
          content: data.content,
          timestamp: data.timestamp
        }
        setComments(prev => [...prev, newComment])
        break

      default:
        console.log('Unknown message type:', data.type)
    }
  }

  // Handle offer from broadcaster
  const handleOffer = async (offerSdp: string) => {
    if (!videoRef.current) return

    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS })
    pcRef.current = pc

    pc.ontrack = (event) => {
      if (videoRef.current && event.streams[0]) {
        videoRef.current.srcObject = event.streams[0]
        videoRef.current.play().catch(console.error)
      }
    }

    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'ice_candidate',
          streamId: parseInt(streamId!),
          candidate: event.candidate.toJSON()
        }))
      }
    }

    await pc.setRemoteDescription(new RTCSessionDescription({
      type: 'offer',
      sdp: offerSdp
    }))

    const answer = await pc.createAnswer()
    await pc.setLocalDescription(answer)

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'answer',
        streamId: parseInt(streamId!),
        answer: answer.sdp
      }))
    }
  }

  // Handle ICE candidate
  const handleIceCandidate = async (candidate: RTCIceCandidateInit) => {
    if (pcRef.current) {
      await pcRef.current.addIceCandidate(new RTCIceCandidate(candidate))
    }
  }

  // Send like
  const sendLike = useCallback(() => {
    if (!isAuthenticated || !streamId) return

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'like',
        streamId: parseInt(streamId),
        userId: user?.id
      }))
    }

    liveStreamService.sendLike(parseInt(streamId)).catch(console.error)
    setLikeCount(prev => prev + 1)
    showFloatingHeart()
  }, [isAuthenticated, streamId, user?.id])

  // Show floating heart animation
  const showFloatingHeart = useCallback(() => {
    const heart = document.createElement('div')
    heart.innerHTML = '❤️'
    heart.className = 'absolute text-2xl animate-bounce'
    heart.style.left = `${Math.random() * 80 + 10}%`
    heart.style.bottom = '20%'
    heart.style.animation = 'floatUp 1s ease-out forwards'

    const container = document.getElementById('hearts-container')
    if (container) {
      container.appendChild(heart)
      setTimeout(() => heart.remove(), 1000)
    }
  }, [])

  // Send comment
  const sendComment = useCallback(() => {
    if (!newComment.trim() || !isAuthenticated || !wsRef.current) return

    const comment: Comment = {
      id: Date.now().toString(),
      userId: user?.id || 0,
      username: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Anonymous',
      content: newComment.trim(),
      timestamp: Date.now()
    }

    wsRef.current.send(JSON.stringify({
      type: 'comment',
      streamId: parseInt(streamId!),
      userId: user?.id,
      content: newComment.trim()
    }))

    setComments(prev => [...prev, comment])
    setNewComment('')
  }, [newComment, isAuthenticated, wsRef, user?.id, user?.firstName, user?.lastName, streamId])

  // Share stream
  const shareStream = useCallback(async () => {
    const url = window.location.href
    const title = stream?.title || 'Live Stream'

    if (navigator.share) {
      try {
        await navigator.share({ title, url })
      } catch (err) {
        console.log('Share cancelled')
      }
    } else {
      navigator.clipboard.writeText(url)
      alert('Link copied to clipboard!')
    }
  }, [stream?.title])

  // Format duration
  const formatDuration = useCallback((seconds: number) => {
    const hrs = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }, [])

  // Scroll comments to bottom
  useEffect(() => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [comments])

  // Initialize
  useEffect(() => {
    fetchStream()

    return () => {
      if (wsRef.current) {
        wsRef.current.send(JSON.stringify({ type: 'viewer_leave' }))
        wsRef.current.close()
      }
      if (pcRef.current) {
        pcRef.current.close()
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current)
      }
      if (streamId) {
        liveStreamService.leaveStream(parseInt(streamId)).catch(console.error)
      }
    }
  }, [fetchStream, streamId])

  // Connect WebSocket after stream is loaded
  useEffect(() => {
    if (stream && !isEnded && !wsRef.current) {
      connectWebSocket()
      durationIntervalRef.current = setInterval(() => {
        setDuration(prev => prev + 1)
      }, 1000)
    }
  }, [stream, isEnded, connectWebSocket])

  // Loading state
  if (loading) {
    return (
      <GovLayout showSidebar={false}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </GovLayout>
    )
  }

  // Error state
  if (error || !stream) {
    return (
      <GovLayout showSidebar={false}>
        <GovPageTitle title="Live Stream" subtitle="Watch live broadcasts" icon={Radio} />
        <GovEmptyState
          icon="📺"
          title={error || 'Stream not found'}
          description="This stream may have ended or is unavailable"
          action={{ label: 'Back to Live Streams', onClick: () => navigate('/live') }}
        />
      </GovLayout>
    )
  }

  return (
    <GovLayout showSidebar={false}>
      {/* CSS for heart animation */}
      <style>{`
        @keyframes floatUp {
          0% { transform: translateY(0) scale(1); opacity: 1; }
          100% { transform: translateY(-100px) scale(1.5); opacity: 0; }
        }
      `}</style>

      {/* Back button */}
      <div className="mb-4">
        <GovButton variant="ghost" onClick={() => navigate('/live')} className="flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back to Live Streams
        </GovButton>
      </div>

      {/* Page title */}
      <GovPageTitle
        title={stream.title || 'Live Stream'}
        subtitle={`Hosted by ${stream.host.firstName} ${stream.host.lastName}`}
        icon={Video}
      />

      {/* Stream info bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {isEnded ? (
          <span className="px-3 py-1 bg-gray-500 text-white text-sm font-medium rounded-full">
            Ended
          </span>
        ) : (
          <span className="flex items-center gap-1.5 px-3 py-1 bg-red-500 text-white text-sm font-medium rounded-full">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
            LIVE
          </span>
        )}
        <span className="flex items-center gap-1.5 px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded-full">
          <Users className="w-4 h-4" />
          {viewerCount} watching
        </span>
        <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded-full">
          {formatDuration(duration)}
        </span>
      </div>

      {/* Main content grid */}
      <div className={`grid grid-cols-1 ${showComments ? 'lg:grid-cols-3' : ''} gap-4`}>
        {/* Video container */}
        <div className={showComments ? 'lg:col-span-2' : ''}>
          <GovCard className="overflow-hidden p-0">
            <div className="relative aspect-video bg-gray-900">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="w-full h-full object-contain"
              />

              {/* Hearts animation container */}
              <div id="hearts-container" className="absolute inset-0 pointer-events-none overflow-hidden" />

              {/* Stream ended overlay */}
              {isEnded && (
                <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center">
                  <h2 className="text-2xl font-bold text-white mb-2">Stream has ended</h2>
                  <p className="text-gray-400 mb-4">Duration: {formatDuration(duration)}</p>
                  <GovButton onClick={() => navigate('/live')}>
                    Browse Live Streams
                  </GovButton>
                </div>
              )}
            </div>

            {/* Actions bar */}
            <div className="p-4 border-t border-gray-100 flex items-center justify-center gap-6">
              <button
                onClick={sendLike}
                disabled={!isAuthenticated}
                className="flex flex-col items-center gap-1 text-gray-600 hover:text-red-500 transition-colors disabled:opacity-50"
              >
                <div className="p-3 rounded-full bg-gray-100 hover:bg-red-50">
                  <Heart className="w-5 h-5" />
                </div>
                <span className="text-xs font-medium">{likeCount}</span>
              </button>

              <button
                onClick={() => setShowComments(!showComments)}
                className={`flex flex-col items-center gap-1 transition-colors ${showComments ? 'text-blue-600' : 'text-gray-600 hover:text-blue-500'}`}
              >
                <div className={`p-3 rounded-full ${showComments ? 'bg-blue-100' : 'bg-gray-100 hover:bg-blue-50'}`}>
                  <MessageCircle className="w-5 h-5" />
                </div>
                <span className="text-xs font-medium">{comments.length}</span>
              </button>

              <button
                onClick={shareStream}
                className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-500 transition-colors"
              >
                <div className="p-3 rounded-full bg-gray-100 hover:bg-blue-50">
                  <Share2 className="w-5 h-5" />
                </div>
                <span className="text-xs font-medium">Share</span>
              </button>
            </div>
          </GovCard>

          {/* Host info */}
          <GovCard className="mt-4">
            <div className="flex items-center gap-3">
              <img
                src={stream.host.profileImage || '/default-avatar.svg'}
                alt={stream.host.firstName}
                className="w-12 h-12 rounded-full object-cover"
              />
              <div className="flex-1">
                <Link to={`/profile/${stream.host.username}`} className="font-semibold text-gray-900 hover:text-blue-600">
                  {stream.host.firstName} {stream.host.lastName}
                </Link>
                <p className="text-sm text-gray-500">{stream.description || 'Live streaming'}</p>
              </div>
            </div>
          </GovCard>
        </div>

        {/* Comments sidebar */}
        {showComments && (
          <div className="lg:col-span-1">
            <GovCard className="h-full flex flex-col">
              {/* Comments header */}
              <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                <h3 className="font-semibold text-gray-900">Comments</h3>
                <button
                  onClick={() => setShowComments(false)}
                  className="p-1 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>

              {/* Comments list */}
              <div className="flex-1 overflow-y-auto py-3 space-y-2 max-h-[400px]">
                {comments.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageCircle className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm text-gray-500">No comments yet</p>
                    <p className="text-xs text-gray-400">Be the first to comment!</p>
                  </div>
                ) : (
                  comments.map((comment) => (
                    <div key={comment.id} className="flex gap-2 py-1">
                      <div className="flex-1">
                        <p className="text-sm">
                          <span className="font-medium text-gray-900">{comment.username}</span>{' '}
                          <span className="text-gray-600">{comment.content}</span>
                        </p>
                      </div>
                    </div>
                  ))
                )}
                <div ref={commentsEndRef} />
              </div>

              {/* Comment input */}
              {isAuthenticated ? (
                <div className="pt-3 border-t border-gray-100">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && sendComment()}
                      placeholder="Add a comment..."
                      className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                    <GovButton
                      onClick={sendComment}
                      disabled={!newComment.trim()}
                      className="!px-3"
                    >
                      <Send className="w-4 h-4" />
                    </GovButton>
                  </div>
                </div>
              ) : (
                <div className="pt-3 border-t border-gray-100 text-center">
                  <Link to="/login" className="text-blue-600 hover:underline text-sm">
                    Login to comment
                  </Link>
                </div>
              )}
            </GovCard>
          </div>
        )}
      </div>
    </GovLayout>
  )
})

WatchLiveStream.displayName = 'WatchLiveStream'

export default WatchLiveStream